<script setup lang="ts">
import type { MwBffAccountSelectionResponse } from '~/types/mw-bff'

/**
 * "Which of these accounts?" — for a user who manages more than one.
 *
 * Extracted for the same reason `TwoFactorStep` was: WHMCS can answer
 * `account_selection_required` to a password login, to a verified second
 * factor, and to a social sign-in, so every surface that signs anybody in has
 * to be able to ask this. It was written out twice already (the login page and
 * the social callback, and the two had drifted — the social one had no owner
 * badge and no selected state), and the checkout was about to make three.
 *
 * Owns the choice and nothing else: the caller makes the call, because which
 * call it is depends on what produced the list.
 */
const props = withDefaults(
  defineProps<{
    accounts: MwBffAccountSelectionResponse['accounts']
    loading?: boolean
    error?: string | null
    showBack?: boolean
    backLabel?: string
    submitLabel?: string
  }>(),
  { loading: false, error: null, showBack: false },
)

const emit = defineEmits<{
  select: [clientId: number]
  back: []
}>()

const selectedClientId = ref<number | null>(null)

/**
 * Seeded from a watcher rather than at setup, because the social path fetches
 * the list after this is already on screen — an initialiser would leave it
 * permanently null and the button permanently disabled.
 */
watch(
  () => props.accounts,
  (accounts) => {
    if (selectedClientId.value == null) {
      selectedClientId.value = accounts?.[0]?.clientId ?? null
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="space-y-4">
    <UAlert v-if="error" color="error" variant="soft" :title="error" />

    <div class="space-y-3">
      <div
        v-for="account in accounts"
        :key="account.clientId"
        class="flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all"
        :class="selectedClientId === account.clientId
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'"
        @click="selectedClientId = account.clientId"
      >
        <div class="flex-1 min-w-0">
          <p class="font-semibold text-gray-900 dark:text-white truncate">{{ account.name }}</p>
          <p class="text-sm text-gray-500 dark:text-gray-400 truncate">{{ account.email }}</p>
          <UBadge v-if="account.isOwner" color="primary" variant="subtle" size="xs" class="mt-1">
            {{ $t('page.login.accountSelection.owner') }}
          </UBadge>
        </div>
        <UIcon
          :name="selectedClientId === account.clientId ? 'i-heroicons-check-circle-solid' : 'i-heroicons-circle'"
          class="w-5 h-5 flex-shrink-0"
          :class="selectedClientId === account.clientId ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'"
        />
      </div>
    </div>

    <div class="flex gap-3 pt-2">
      <UButton
        v-if="showBack"
        variant="outline"
        size="xl"
        class="flex-1"
        :disabled="loading"
        @click="emit('back')"
      >
        {{ backLabel ?? $t('general.back') }}
      </UButton>
      <UButton
        size="xl"
        class="flex-1 shadow-lg"
        :loading="loading"
        :disabled="!selectedClientId"
        trailing-icon="i-heroicons-arrow-right-20-solid"
        @click="selectedClientId && emit('select', selectedClientId)"
      >
        {{ submitLabel ?? $t('page.login.continue') }}
      </UButton>
    </div>
  </div>
</template>
